import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ToastModal from "../../components/ToastModal/ToastModal";
import TopBarProfile from "../../components/TopBarProfile/TopBarProfile";
import {
  Container,
  Header,
  Title,
  Ornament,
  OrnLine,
  OrnDiamond,
  Form,
  Label,
  Input,
  SubmitButton,
  Note,
  Row,
  CheckboxContainer,
  CheckboxLabel,
  Checkbox,
  CheckboxError,
  SectionCard,
  DateInputWrap,
  DateInput,
  CalendarIcon,
} from "./ProfilePageStyles";

import { FaRegCalendarAlt } from "react-icons/fa";

const luniMap = {
  Ianuarie: "01",
  Februarie: "02",
  Martie: "03",
  Aprilie: "04",
  Mai: "05",
  Iunie: "06",
  Iulie: "07",
  August: "08",
  Septembrie: "09",
  Octombrie: "10",
  Noiembrie: "11",
  Decembrie: "12",
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const topRef = useRef(null);

  const [form, setForm] = useState({
    id: "",
    nume: "",
    prenume: "",
    email: "",
    telefon: "",
    data_nasterii: "",
    prelucrare_date: true,
    primire_oferte: false,
  });

  const [errors, setErrors] = useState({});
  const [toastMsg, setToastMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const storedUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case "nume":
        return /^[a-zA-ZăîâșțĂÎÂȘȚ\s\-]+$/.test(value)
          ? ""
          : "Introdu un nume valid (doar litere).";
      case "prenume":
        return /^[a-zA-ZăîâșțĂÎÂȘȚ\s\-]+$/.test(value)
          ? ""
          : "Introdu un prenume valid (doar litere).";
      case "telefon":
        return /^\d{10}$/.test(value)
          ? ""
          : "Te rugăm să introduci un număr de telefon valid (10 cifre).";
      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.nume.match(/^[a-zA-ZăîâșțĂÎÂȘȚ\s\-]+$/)) {
      newErrors.nume = "Introdu un nume valid (doar litere).";
    }

    if (!form.prenume.match(/^[a-zA-ZăîâșțĂÎÂȘȚ\s\-]+$/)) {
      newErrors.prenume = "Introdu un prenume valid (doar litere).";
    }

    if (!/^\d{10}$/.test(form.telefon)) {
      newErrors.telefon =
        "Te rugăm să introduci un număr de telefon valid (10 cifre).";
    }

    if (!form.data_nasterii) {
      newErrors.data_nasterii = "Completează data nașterii.";
    }

    if (!form.prelucrare_date) {
      newErrors.prelucrare_date =
        "Pentru păstrarea contului, acordul pentru prelucrarea datelor trebuie să rămână activ.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (!storedUser?.email) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(process.env.REACT_APP_GRAPHQL_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: `
              query UserProfile($email: String!) {
                userProfile(email: $email) {
                  id
                  nume
                  prenume
                  email
                  telefon
                  data_nasterii
                  prelucrare_date
                  primire_oferte
                  role
                  confirmed
                }
              }
            `,
            variables: {
              email: storedUser.email,
            },
          }),
        });

        const result = await response.json();

        if (result.errors) {
          console.error(result.errors);
          setToastMsg("Nu am putut încărca profilul.");
          return;
        }

        const profile = result?.data?.userProfile;

        if (!profile) {
          setToastMsg("Profilul nu a fost găsit.");
          return;
        }

        setForm({
          id: profile.id || "",
          nume: profile.nume || "",
          prenume: profile.prenume || "",
          email: profile.email || "",
          telefon: profile.telefon || "",
          data_nasterii: profile.data_nasterii || "",
          prelucrare_date: Boolean(profile.prelucrare_date),
          primire_oferte: Boolean(profile.primire_oferte),
        });
      } catch (error) {
        console.error("Eroare fetch profil:", error);
        setToastMsg("A apărut o eroare internă la încărcarea profilului.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate, storedUser]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const actualValue = type === "checkbox" ? checked : value;

    setForm((prev) => ({
      ...prev,
      [name]: actualValue,
    }));

    if (type !== "checkbox") {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, actualValue),
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);

    try {
      const response = await fetch(process.env.REACT_APP_GRAPHQL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            mutation UpdateUserProfile($id: ID!, $input: UpdateUserProfileInput!) {
              updateUserProfile(id: $id, input: $input) {
                message
                user {
                  id
                  nume
                  prenume
                  email
                  role
                }
              }
            }
          `,
          variables: {
            id: form.id,
            input: {
              nume: form.nume,
              prenume: form.prenume,
              telefon: form.telefon,
              data_nasterii: form.data_nasterii,
              prelucrare_date: form.prelucrare_date,
              primire_oferte: form.primire_oferte,
            },
          },
        }),
      });

      const result = await response.json();

      if (result.errors) {
        console.error("GraphQL update errors:", result.errors);
        setToastMsg(
          result.errors[0]?.message || "Eroare la actualizarea profilului.",
        );
        return;
      }

      const updatedUser = result?.data?.updateUserProfile?.user;

      if (updatedUser) {
        const existing = (() => {
          try {
            return JSON.parse(localStorage.getItem("user")) || {};
          } catch {
            return {};
          }
        })();

        localStorage.setItem(
          "user",
          JSON.stringify({
            ...existing,
            id: updatedUser.id,
            email: updatedUser.email,
            role: updatedUser.role,
            nume: updatedUser.nume,
            prenume: updatedUser.prenume,
          }),
        );
      }

      setToastMsg(
        result?.data?.updateUserProfile?.message ||
          "Profil actualizat cu succes!",
      );
    } catch (error) {
      console.error("Eroare update profil:", error);
      setToastMsg("A apărut o eroare internă.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <TopBarProfile activeTab="profile" />
        <Container ref={topRef}>
          <Header>
            <Title>Profilul meu</Title>
            <Ornament aria-hidden="true">
              <OrnLine />
              <OrnDiamond />
              <OrnLine />
            </Ornament>
          </Header>
          <Note>Se încarcă datele profilului...</Note>
        </Container>
      </>
    );
  }

  return (
    <>
      <TopBarProfile activeTab="profile" />

      <Container ref={topRef}>
        <SectionCard>
          <Header>
            <Title>Profilul meu</Title>
            <Ornament aria-hidden="true">
              <OrnLine />
              <OrnDiamond />
            </Ornament>
          </Header>
          <Form onSubmit={handleSubmit}>
            <Row>
              <div>
                <Label>Prenume *</Label>
                <Input
                  name="prenume"
                  value={form.prenume}
                  onChange={handleChange}
                  style={errors.prenume ? { border: "1px solid red" } : {}}
                />
                {errors.prenume && (
                  <span style={{ color: "red", fontSize: "0.82rem" }}>
                    {errors.prenume}
                  </span>
                )}
              </div>

              <div>
                <Label>Nume *</Label>
                <Input
                  name="nume"
                  value={form.nume}
                  onChange={handleChange}
                  style={errors.nume ? { border: "1px solid red" } : {}}
                />
                {errors.nume && (
                  <span style={{ color: "red", fontSize: "0.82rem" }}>
                    {errors.nume}
                  </span>
                )}
              </div>
            </Row>

            <div>
              <Label>E-mail *</Label>
              <Input name="email" type="email" value={form.email} disabled />
              <Note>Emailul nu se modifică din această pagină.</Note>
            </div>

            <div>
              <Label>Număr de telefon *</Label>
              <Input
                name="telefon"
                value={form.telefon}
                onChange={handleChange}
                style={errors.telefon ? { border: "1px solid red" } : {}}
              />
              {errors.telefon && (
                <span style={{ color: "red", fontSize: "0.82rem" }}>
                  {errors.telefon}
                </span>
              )}
            </div>

            <div>
              <Label>Data nașterii *</Label>
              <DateInputWrap>
                <DateInput
                  name="data_nasterii"
                  value={form.data_nasterii}
                  onChange={handleChange}
                  style={
                    errors.data_nasterii ? { border: "1px solid red" } : {}
                  }
                />

                <CalendarIcon aria-hidden="true">
                  <FaRegCalendarAlt />
                </CalendarIcon>
              </DateInputWrap>

              {errors.data_nasterii && (
                <span style={{ color: "red", fontSize: "0.82rem" }}>
                  {errors.data_nasterii}
                </span>
              )}
            </div>

            <CheckboxContainer>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="prelucrare_date"
                  checked={form.prelucrare_date}
                  onChange={handleChange}
                />
                <span>
                  Sunt de acord ca datele mele să fie prelucrate conform
                  politicii de confidențialitate.*
                </span>
              </CheckboxLabel>
              {errors.prelucrare_date && (
                <CheckboxError>{errors.prelucrare_date}</CheckboxError>
              )}

              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  name="primire_oferte"
                  checked={form.primire_oferte}
                  onChange={handleChange}
                />
                <span>
                  Sunt de acord să primesc oferte și promoții prin e-mail sau
                  SMS.
                </span>
              </CheckboxLabel>
            </CheckboxContainer>

            <SubmitButton type="submit" disabled={saving}>
              {saving ? "Se salvează..." : "Salvează datele"}
            </SubmitButton>

            <Note>
              Parola se poate schimba separat din fluxul „Ai uitat parola?”.
            </Note>
          </Form>
        </SectionCard>

        {toastMsg && (
          <ToastModal message={toastMsg} onClose={() => setToastMsg("")} />
        )}
      </Container>
    </>
  );
}
